import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../analytics/analytics';

const ServicesSection = styled.section`
  padding: 100px 0;
  background: white;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  display: inline-block;
  width: 100%;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: var(--primary-orange);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-orange), var(--primary-blue));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: var(--primary-orange);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  &:nth-child(1) {
    animation: slideInLeft 0.6s ease-out;
  }
  
  &:nth-child(2) {
    animation: slideInUp 0.6s ease-out 0.2s both;
  }
  
  &:nth-child(3) {
    animation: slideInRight 0.6s ease-out 0.4s both;
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(50px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const ServiceIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  text-align: center;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-orange), var(--primary-blue));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: transform 0.3s ease;
  
  ${ServiceCard}:hover & {
    transform: scale(1.1);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 1rem;
  text-align: center;
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  margin-bottom: 2rem;
  text-align: center;
`;

const ServiceButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, var(--primary-blue) 0%, #1e40af 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(30, 64, 175, 0.3);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Services: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Smoothly scroll to the demo lesson form if present on the page.
  const scrollToDemoLessonForm = useCallback(() => {
    const targetId = 'demo-lesson';
    const el = typeof document !== 'undefined' ? document.getElementById(targetId) : null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Fallback: navigate to Home with hash so the browser can jump to the anchor
      navigate(`/#${targetId}`);
    }
  }, [navigate]);

  const services = [
    {
      icon: '🚗',
      titleKey: 'services.autorijles.title',
      descriptionKey: 'services.autorijles.description',
      buttonKey: 'services.autorijles.button',
      action: () => { trackEvent('cta_services_click', { id: 'autorijles' }); navigate('/auto'); }
    },
    {
      icon: '🌍',
      titleKey: 'services.expaten.title',
      descriptionKey: 'services.expaten.description',
      buttonKey: 'services.expaten.button',
      action: () => { trackEvent('cta_services_click', { id: 'expaten_scroll_demo_form' }); scrollToDemoLessonForm(); }
    },
    {
      icon: '📚',
      titleKey: 'services.theorie.title',
      descriptionKey: 'services.theorie.description',
      buttonKey: 'services.theorie.button',
      action: () => { trackEvent('cta_services_click', { id: 'theorie_external' }); window.open('https://detheorieprofessor.nl/', '_blank'); }
    }
  ];

  return (
    <ServicesSection id="services">
      <Container>
        <SectionTitle>{t('services.title')}</SectionTitle>
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard key={index}>
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{t(service.titleKey)}</ServiceTitle>
              <ServiceDescription>{t(service.descriptionKey)}</ServiceDescription>
              <ServiceButton onClick={service.action}>
                {t(service.buttonKey)}
              </ServiceButton>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services;